import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import ShoppingCartCheckoutRoundedIcon from '@mui/icons-material/ShoppingCartCheckoutRounded';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useCheckoutSettings, useUpdateCheckoutSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import {
  ChipListField,
  FeedbackSnackbar,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';

/** Checkout flow configuration. */
export function CheckoutSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage } = useDomainPermissions('checkout');
  const query = useCheckoutSettings();
  const update = useUpdateCheckoutSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success(t('checkoutSettings.savedFeedback')),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('checkoutSettings.title')}
      subtitle={t('checkoutSettings.subtitle')}
      icon={<ShoppingCartCheckoutRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title={t('checkoutSettings.flowSection.title')} icon={<ShoppingCartCheckoutRoundedIcon />}>
              <SwitchField
                label={t('checkoutSettings.flowSection.guestCheckoutLabel')}
                description={t('checkoutSettings.flowSection.guestCheckoutDescription')}
                checked={draft.guestCheckoutEnabled}
                onChange={(value) => form.setField('guestCheckoutEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('checkoutSettings.flowSection.promoCodesLabel')}
                description={t('checkoutSettings.flowSection.promoCodesDescription')}
                checked={draft.promoCodesEnabled}
                onChange={(value) => form.setField('promoCodesEnabled', value)}
                disabled={disabled}
              />
              <ChipListField
                label={t('checkoutSettings.flowSection.stepsLabel')}
                description={t('checkoutSettings.flowSection.stepsDescription')}
                value={draft.steps}
                onChange={(value) => form.setField('steps', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard
              title={t('checkoutSettings.requiredFieldsSection.title')}
              description={t('checkoutSettings.requiredFieldsSection.description')}
              icon={<ChecklistRoundedIcon />}
            >
              <ChipListField
                label={t('checkoutSettings.requiredFieldsSection.customerFieldsLabel')}
                value={draft.requiredCustomerFields}
                onChange={(value) => form.setField('requiredCustomerFields', value)}
                disabled={disabled}
              />
              <ChipListField
                label={t('checkoutSettings.requiredFieldsSection.deliveryFieldsLabel')}
                value={draft.requiredDeliveryFields}
                onChange={(value) => form.setField('requiredDeliveryFields', value)}
                disabled={disabled}
              />
              <ChipListField
                label={t('checkoutSettings.requiredFieldsSection.paymentFieldsLabel')}
                value={draft.requiredPaymentFields}
                onChange={(value) => form.setField('requiredPaymentFields', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title={t('checkoutSettings.selectionRulesSection.title')} icon={<RuleRoundedIcon />}>
              <TextInputField
                label={t('checkoutSettings.selectionRulesSection.deliveryMethodLabel')}
                description={t('checkoutSettings.selectionRulesSection.deliveryMethodDescription')}
                value={draft.deliveryMethodSelectionRule}
                onChange={(value) => form.setField('deliveryMethodSelectionRule', value)}
                disabled={disabled}
              />
              <TextInputField
                label={t('checkoutSettings.selectionRulesSection.paymentMethodLabel')}
                description={t('checkoutSettings.selectionRulesSection.paymentMethodDescription')}
                value={draft.paymentMethodSelectionRule}
                onChange={(value) => form.setField('paymentMethodSelectionRule', value)}
                disabled={disabled}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
